import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { DialogProps, StyleClasses } from '@paintscout/ui'
import { Dialog, DialogTitle, DialogActions, DialogContent, Button, Typography, InputField } from '@paintscout/ui'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import ClearIcon from '@material-ui/icons/Clear'
import type { AutomationOption } from '@paintscout/util/builder'

const useStyles = makeStyles<Theme, AutomationRenameDialogProps>(
  (theme) => ({
    root: {},
    dialogTitle: {},
    dialogContent: {
      paddingBottom: `${theme.spacing(3)}px`
    },
    closeIcon: {
      cursor: 'pointer',
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(2)
    },
    cancel: {},
    confirm: {}
  }),
  { name: 'AutomationRenameDialog' }
)

export interface AutomationRenameDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  automation: AutomationOption
  onConfirm: (automation: AutomationOption) => any
  onCancel: (event: React.MouseEvent<HTMLElement>) => any
}

function AutomationRenameDialog(props: AutomationRenameDialogProps) {
  const classes = useStyles(props)
  const { onConfirm, onCancel, automation, ...baseDialogProps } = props
  const { enqueueSnackbar } = useSnackbar()
  const [isLoading, setIsLoading] = useState(false)
  const [automationName, setAutomationName] = useState(automation.name)

  const rightContent = (
    <div onClick={onCancel} className={classes.closeIcon}>
      <ClearIcon />
    </div>
  )

  return (
    <Dialog {...baseDialogProps}>
      <DialogTitle classes={{ root: classes.dialogTitle }} rightContent={rightContent}>
        <Typography variant="h2" weight={'normal'}>
          Rename Automation
        </Typography>
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <InputField
          label="Name"
          value={automationName}
          onChange={(e) => setAutomationName(e.target.value)}
          placeholder="Text"
          autoFocus
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button
          className={classes.confirm}
          variant={'contained'}
          disabled={isLoading}
          data-testid="automation-rename-dialog-confirm-button"
          onClick={handleSubmit}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )

  async function handleSubmit() {
    setIsLoading(true)
    try {
      onConfirm({
        ...automation,
        name: automationName
      })
    } catch (error) {
      Sentry.captureException(error)
      enqueueSnackbar('error', { variant: 'error' })
    }
    setIsLoading(false)
  }
}

export default AutomationRenameDialog
